import {
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Box,
  FormHelperText,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import FileUpload from "../../GovernanceDashboard/DataEntry/FileUpload";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import styles from "../../../styles/social/social.module.css";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { get, put } from "../../../utils/api";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import SocialLayout from "../../../Layout/SocialLayout";
import { textError } from "../../../utils/validationMessage";
import {
  annualPolicyYesFieldErrors,
  getEffectiveDateMinMax,
  hasUploadedDocument,
  isAnnualPolicyUploadDisabled,
  shouldShowAnnualPolicyRevised,
} from "../../../utils/annualPolicyFormHelpers";
import WestIcon from "@mui/icons-material/West";
import { TurnSharpLeftRounded } from "@mui/icons-material";

const SOCIAL_PRIMARY = "#1C486B";
const socialRadioSx = {
  color: SOCIAL_PRIMARY,
  "&.Mui-checked": { color: SOCIAL_PRIMARY },
};
const backButtonSx = {
  color: SOCIAL_PRIMARY,
  textTransform: "none",
  border: `1px solid ${SOCIAL_PRIMARY}`,
  "&:hover": { borderColor: SOCIAL_PRIMARY },
};
const primaryButtonSx = {
  textTransform: "none",
  backgroundColor: SOCIAL_PRIMARY,
  "&:hover": { backgroundColor: "#163a56" },
};

const removeCommas = (val) => {
  if (val === null || val === undefined) return "";
  return val.toString().replace(/,/g, "").trim();
};

const formatWithCommas = (val) => {
  const normalized = removeCommas(val);
  if (normalized === "") return "";
  if (!/^\d*\.?\d*$/.test(normalized)) return normalized;
  const [integerPart = "", decimalPart] = normalized.split(".");
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimalPart !== undefined
    ? `${formattedInteger}.${decimalPart}`
    : formattedInteger;
};

const stripCommasFromNumericStrings = (input) => {
  if (Array.isArray(input)) return input.map(stripCommasFromNumericStrings);
  if (input && typeof input === "object") {
    return Object.fromEntries(
      Object.entries(input).map(([key, value]) => [
        key,
        stripCommasFromNumericStrings(value),
      ]),
    );
  }
  if (typeof input === "string" && /^\d{1,3}(,\d{3})*(\.\d+)?$/.test(input)) {
    return removeCommas(input);
  }
  return input;
};

const NumericInputField = ({
  label,
  name,
  value,
  errors,
  onChange,
  placeholder,
  disabled,
}) => (
  <div style={{ marginBottom: "1rem" }}>
    <p className={styles.form_heading} style={{ fontWeight: "bold" }}>
      {label}
    </p>
    <TextField
      name={name}
      value={formatWithCommas(value)}
      onChange={onChange}
      variant="outlined"
      size="small"
      sx={{ pl: 2, width: "50%" }}
      placeholder={placeholder}
      error={errors?.[name]}
      disabled={disabled ?? false}
    />
    {errors?.[name] && (
      <FormHelperText
        sx={{ color: "#D34040", marginLeft: "1rem", fontSize: "14px" }}
      >
        {errors?.[name]}
      </FormHelperText>
    )}
  </div>
);

const BooleanInputField = ({ label, name, value, onChange }) => (
  <FormControl
    component="fieldset"
    style={{
      marginBottom: "1rem",
      display: label === "Is the Policy revised?" ? "flex" : undefined,
    }}
  >
    <p className={styles.form_heading} style={{ fontWeight: "bold" }}>
      {label}
    </p>
    <RadioGroup
      row
      name={name}
      value={value === false || value === "false" ? "false" : "true"}
      onChange={onChange}
      sx={{ pl: 2 }}
    >
      <FormControlLabel
        value="true"
        control={<Radio sx={socialRadioSx} />}
        label="Yes"
      />
      <FormControlLabel
        value="false"
        control={<Radio sx={socialRadioSx} />}
        label="No"
      />
    </RadioGroup>
  </FormControl>
);

const HumanRights = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [errors, setErrors] = useState({});
  const [module, setModule] = useState("monthly");
  const [existingHumanRightsPolicyDocUrl, setExistingHumanRightsPolicyDocUrl] =
    useState("");
  const [prevHumanRightsPolicy, setPrevHumanRightsPolicy] = useState({
    documentUrl: "",
    remark: "",
    effectiveDate: "",
  });
  const [isHumanRightsPolicyDocDeleted, setIsHumanRightsPolicyDocDeleted] =
    useState(false);

  // Annual formData
  const annualFormDataInitial = {
    humanRightsPolicy: {
      status: true,
      isRevised: false,
      documentUrl: "",
      remark: "",
      statusremark: "",
      effectiveDate: "",
    },
    supplyChainAssessments: {
      percentage: "",
      additionalDetails: "",
    },
    grievanceMechanism: {
      status: true,
      forCommunity: {
        status: true,
        documentUrl: "",
        remark: "",
      },
      forEmployeeWorkers: {
        status: true,
        documentUrl: "",
        remark: "",
      },
    },
  };

  // Monthly formData
  const monthlyFormDataInitial = {
    incidentsReported: "",
    incidentsResolved: "",
    incidentsResolutionRate: "",
  };

  const [annualFormData, setAnnualFormData] = useState(annualFormDataInitial);
  const [isFirstEntry, setIsFirstEntry] = useState(false);
  /** Previous reporting year policy document only (from annualRangeWithPrev policies). */
  const [previousYearHumanRightsDoc, setPreviousYearHumanRightsDoc] = useState("");
  const [entryYear, setEntryYear] = useState(null);
  const todayIso = new Date().toISOString().split("T")[0];
  const currentYear = new Date().getFullYear();
  const maxEffectiveDate =
    Number(entryYear) && Number(entryYear) < currentYear
      ? `${entryYear}-12-31`
      : todayIso;
  const { min: policyEffectiveMin, max: policyEffectiveMax } =
    getEffectiveDateMinMax(entryYear);
  const [monthlyFormData, setMonthlyFormData] = useState(
    monthlyFormDataInitial,
  );

  // Get the appropriate formData based on module
  const formData = module === "annual" ? annualFormData : monthlyFormData;
  const setFormData =
    module === "annual" ? setAnnualFormData : setMonthlyFormData;

  const validation = () => {
    let newErrors = {}; // Initialize an empty error object
    let hasErrors = false;

    const isEmpty = (value) =>
      value === "" || value === null || value === undefined;

    const validateRecursive = (data, errorsObj) => {
      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          let arrayErrors = value
            .map((item, index) => {
              if (typeof item === "object") {
                let nestedErrors = {};
                validateRecursive(item, nestedErrors);
                return Object.keys(nestedErrors).length > 0
                  ? nestedErrors
                  : null;
              } else {
                return isEmpty(item) ? textError : null;
              }
            })
            .filter((error) => error !== null);

          if (arrayErrors.length > 0) {
            errorsObj[key] = arrayErrors;
            hasErrors = true;
          }
        } else if (typeof value === "object" && value !== null) {
          let nestedErrors = {};

          // humanRightsPolicy: annual Yes rules applied after validateRecursive (module === "annual")
          if (key === "supplyChainAssessments") {
            if (isEmpty(value.percentage)) {
              nestedErrors = textError;
              hasErrors = true;
            }
          }
          // Validate grievanceMechanism
          else if (key === "grievanceMechanism") {
            if (value.forCommunity?.status === true) {
              if (isEmpty(value.forCommunity.documentUrl)) {
                nestedErrors.forCommunity = { documentUrl: textError };
                hasErrors = true;
              }
            }
            if (value.forEmployeeWorkers?.status === true) {
              if (isEmpty(value.forEmployeeWorkers.documentUrl)) {
                nestedErrors.forEmployeeWorkers = { documentUrl: textError };
                hasErrors = true;
              }
            }
          } else if (key === "humanRightsPolicy") {
            // Annual human rights policy Yes-path validated after validateRecursive
          } else {
            validateRecursive(value, nestedErrors);
          }

          if (Object.keys(nestedErrors).length > 0) {
            errorsObj[key] = nestedErrors;
            hasErrors = true;
          }
        } else if (
          isEmpty(value) &&
          key !== "incidentsResolutionRate" &&
          key !== "remark" &&
          key !== "additionalDetails"
        ) {
          errorsObj[key] = textError;
          hasErrors = true;
        }
      });
    };

    // Only validate fields relevant to the current module
    if (module === "annual") {
      validateRecursive(annualFormData, newErrors);
      const hrp = annualFormData.humanRightsPolicy;
      if (hrp?.status === true) {
        const showRev = shouldShowAnnualPolicyRevised(
          !isFirstEntry,
          previousYearHumanRightsDoc,
        );
        const polErr = annualPolicyYesFieldErrors(hrp, entryYear, showRev);
        if (Object.keys(polErr).length > 0) {
          newErrors.humanRightsPolicy = {
            ...(typeof newErrors.humanRightsPolicy === "object" &&
            newErrors.humanRightsPolicy
              ? newErrors.humanRightsPolicy
              : {}),
            ...polErr,
          };
          hasErrors = true;
        } else if (typeof newErrors.humanRightsPolicy === "string") {
          delete newErrors.humanRightsPolicy;
        }
      }
    } else {
      validateRecursive(monthlyFormData, newErrors);
    }

    // ✅ Remove `documentUrl` completely if `status: false`
    if (module === "annual") {
      ["humanRightsPolicy"].forEach((key) => {
        if (annualFormData[key]?.status === false) {
          delete newErrors[key];
        }
      });
    }
    setErrors(newErrors);
    return hasErrors;
  };

  const fetchData = useCallback(
    async (id) => {
      try {
        const response = await get(`/social/getSocial?id=${id}`);
        const responseData = response?.data?.data;
        const socialData = Array.isArray(responseData)
          ? responseData[0]
          : responseData;
        const fetchedData = socialData?.humanRights || {};
        const sanitizeDocumentUrl = (value) => {
          if (typeof value !== "string") return value || "";
          const cleaned = value.trim();
          if (!cleaned) return "";
          if (cleaned.toLowerCase() === "no documents available") return "";
          return cleaned;
        };
        const parseBoolean = (val, defaultValue = false) => {
          if (val === undefined || val === null) return defaultValue;
          if (typeof val === "boolean") return val;
          if (typeof val === "string") {
            const normalized = val.trim().toLowerCase();
            if (normalized === "true") return true;
            if (normalized === "false") return false;
          }
          return Boolean(val);
        };
        const currentEntryYear =
          Number(socialData?._socialDataRef?.yearToSubmit) ||
          Number(socialData?.yearToSubmit) ||
          Number(socialData?.year) ||
          undefined;
        setEntryYear(currentEntryYear || null);
        const companyId = socialData?.company;
        let prevHumanPolicy = {};
        try {
          const qs = new URLSearchParams();
          if (currentEntryYear) qs.set("year", String(currentEntryYear));
          if (companyId) qs.set("companyId", String(companyId));
          const prevUrl = `/social/totalWorkforce/annualRangeWithPrev${qs.toString() ? `?${qs.toString()}` : ""}`;
          const prevRes = await get(prevUrl);
          setIsFirstEntry(Boolean(prevRes?.data?.data?.isFirstEntry));
          const prevPolicies = prevRes?.data?.data?.policies || {};
          prevHumanPolicy = prevPolicies?.["humanRights.humanRightsPolicy"] || {};
          setPreviousYearHumanRightsDoc(
            sanitizeDocumentUrl(prevHumanPolicy?.documentUrl || ""),
          );
        } catch (e) {
          prevHumanPolicy = {};
          setIsFirstEntry(false);
          setPreviousYearHumanRightsDoc("");
        }

        // Get module from URL params, API response, or default to monthly
        const urlModule = searchParams.get("entryType");
        const apiModule =
          socialData?.module || socialData?._socialDataRef?.module;
        let finalModule = "monthly";

        if (
          urlModule === "annual" ||
          apiModule === "Annual" ||
          apiModule === "annual"
        ) {
          finalModule = "annual";
        } else if (
          urlModule === "monthly" ||
          apiModule === "Monthly" ||
          apiModule === "monthly"
        ) {
          finalModule = "monthly";
        }

        setModule(finalModule);

        // Separate annual and monthly data
        const annualData = {};
        const monthlyData = {};

        if (fetchedData) {
          // Annual fields
          const annualFields = [
            "humanRightsPolicy",
            "supplyChainAssessments",
            "grievanceMechanism",
          ];

          // Monthly fields
          const monthlyFields = [
            "incidentsReported",
            "incidentsResolved",
            "incidentsResolutionRate",
          ];

          Object.keys(fetchedData).forEach((key) => {
            if (annualFields.includes(key)) {
              annualData[key] = fetchedData[key];
            } else if (monthlyFields.includes(key)) {
              monthlyData[key] = formatWithCommas(fetchedData[key]);
            }
          });
        }

        // Default annual radio statuses to "Yes" when missing from API payload
        {
          const currentPolicy =
            annualData.humanRightsPolicy &&
            typeof annualData.humanRightsPolicy === "object"
              ? annualData.humanRightsPolicy
              : {};
          const prevDocUrl = sanitizeDocumentUrl(prevHumanPolicy?.documentUrl || "");
          annualData.humanRightsPolicy = {
            ...currentPolicy,
            status:
              currentPolicy.status === undefined
                ? parseBoolean(
                  // prevHumanPolicy?.status, 
                  true)
                : parseBoolean(currentPolicy.status, true),
            isRevised:
              currentPolicy.isRevised === undefined
                ? parseBoolean(prevHumanPolicy?.isRevised, false)
                : parseBoolean(currentPolicy.isRevised, false),
            documentUrl:
              sanitizeDocumentUrl(currentPolicy.documentUrl || "") || prevDocUrl,
            remark:
              typeof currentPolicy.remark === "string" &&
              currentPolicy.remark.trim() !== ""
                || currentPolicy.remark||
                  prevHumanPolicy?.remark || "",
                
            effectiveDate:
              currentPolicy.effectiveDate || prevHumanPolicy?.effectiveDate || "",
          };
        }
        setExistingHumanRightsPolicyDocUrl(
          sanitizeDocumentUrl(annualData?.humanRightsPolicy?.documentUrl || "") ||
            sanitizeDocumentUrl(prevHumanPolicy?.documentUrl || ""),
        );
        setPrevHumanRightsPolicy({
          documentUrl:
            sanitizeDocumentUrl(annualData?.humanRightsPolicy?.documentUrl || "") ||
            sanitizeDocumentUrl(prevHumanPolicy?.documentUrl || ""),
          remark:
            annualData?.humanRightsPolicy?.remark || prevHumanPolicy?.remark || "",
          effectiveDate:
            annualData?.humanRightsPolicy?.effectiveDate ||
            prevHumanPolicy?.effectiveDate ||
            "",
        });

        if (
          annualData.grievanceMechanism &&
          typeof annualData.grievanceMechanism === "object"
        ) {
          annualData.grievanceMechanism = {
            ...annualData.grievanceMechanism,
            status:
              annualData.grievanceMechanism.status === undefined
                ? true
                : annualData.grievanceMechanism.status,
            forCommunity: {
              ...(annualData.grievanceMechanism.forCommunity || {}),
              status:
                annualData.grievanceMechanism.forCommunity?.status === undefined
                  ? true
                  : annualData.grievanceMechanism.forCommunity.status,
            },
            forEmployeeWorkers: {
              ...(annualData.grievanceMechanism.forEmployeeWorkers || {}),
              status:
                annualData.grievanceMechanism.forEmployeeWorkers?.status ===
                undefined
                  ? true
                  : annualData.grievanceMechanism.forEmployeeWorkers.status,
            },
          };
        }

        // Update annual form data
        setAnnualFormData((prevState) => ({
          ...prevState,
          ...annualData,
        }));

        // Update monthly form data
        setMonthlyFormData((prevState) => ({
          ...prevState,
          ...monthlyData,
        }));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    },
    [searchParams],
  );

  useEffect(() => {
    fetchData(id);
  }, [id, fetchData]);

  const handleNestedBooleanChange = (e) => {
    const { name, value } = e.target;
    const parts = name.split(".");
    setFormData((prev) => {
      let updated = { ...prev };
      let current = updated;

      // Navigate to the nested object
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) {
          current[parts[i]] = {};
        }
        current = current[parts[i]];
      }

      // Set the final value
      const finalKey = parts[parts.length - 1];
      current[finalKey] = value === "true";

      const isHumanRightsPolicy = parts[0] === "humanRightsPolicy";

      if (
        isHumanRightsPolicy &&
        finalKey === "isRevised" &&
        current[finalKey] === true
      ) {
        current.documentUrl = "";
        current.remark = "";
        current.effectiveDate = "";
      }
      if (
        isHumanRightsPolicy &&
        finalKey === "isRevised" &&
        current[finalKey] === false
      ) {
        setIsHumanRightsPolicyDocDeleted(false);
        current.documentUrl =
          existingHumanRightsPolicyDocUrl ||
          prevHumanRightsPolicy.documentUrl ||
          current.documentUrl ||
          "";
        current.effectiveDate =
          prevHumanRightsPolicy.effectiveDate || current.effectiveDate || "";
        current.remark = prevHumanRightsPolicy.remark || current.remark || "";
      }

      // Clear documentUrl if status is false
      if (finalKey === "status" && current[finalKey] === false) {
        if (current.documentUrl !== undefined) {
          current.documentUrl = "";
        }
        if (current.effectiveDate !== undefined) {
          current.effectiveDate = "";
        }
        if (current.remark !== undefined) {
          current.remark = "";
        }
        if (isHumanRightsPolicy) {
          current.isRevised = false;
        }
      }

      return updated;
    });
    setErrors((prev) => {
      const next = { ...prev };
      if (parts[0] === "humanRightsPolicy") {
        delete next.humanRightsPolicy;
      } else {
        next[parts[0]] = "";
      }
      return next;
    });
  };

  const handleFileChange = (file, policy, nestedKey = null) => {
    if (policy === "humanRightsPolicy" && !nestedKey) {
      setIsHumanRightsPolicyDocDeleted(!hasUploadedDocument(file));
    }

    setFormData((prev) => {
      if (nestedKey) {
        return {
          ...prev,
          [policy]: {
            ...prev[policy],
            [nestedKey]: {
              ...prev[policy][nestedKey],
              documentUrl: file,
            },
          },
        };
      }
      return {
        ...prev,
        [policy]: {
          ...prev[policy],
          documentUrl: file,
        },
      };
    });
    if (policy === "humanRightsPolicy" && hasUploadedDocument(file)) {
      setErrors((prev) => {
        const next = { ...prev };
        const cur =
          typeof next.humanRightsPolicy === "object" && next.humanRightsPolicy
            ? { ...next.humanRightsPolicy }
            : {};
        delete cur.documentUrl;
        if (Object.keys(cur).length) next.humanRightsPolicy = cur;
        else delete next.humanRightsPolicy;
        return next;
      });
    } else if (policy !== "humanRightsPolicy") {
      setErrors((prev) => ({
        ...prev,
        [policy]: "",
      }));
    }
  };

  // const handleNumberChange = (e) => {
  //   const { name, value } = e.target;
  //   setFormData((prev) => ({
  //     ...prev,
  //     [name]: value ? Number(value) : "",
  //   }));
  // };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    const normalizedValue = removeCommas(value);
    if (!/^\d*$/.test(normalizedValue)) return;

    let newValue = normalizedValue === "" ? "" : formatWithCommas(normalizedValue);
    setMonthlyFormData((prev) => {
      let updated = { ...prev, [name]: newValue };
      if (name === "incidentsReported" && newValue === "") {
        updated.incidentsResolved = "";
        updated.incidentsResolutionRate = "";
        return updated;
      }
      if (name === "incidentsResolved" && newValue === "") {
        updated.incidentsResolutionRate = "";
        return updated;
      }
      // Only validate and calculate if both fields are present and numbers
      const reported =
        name === "incidentsReported" ? newValue : prev.incidentsReported;
      const resolved =
        name === "incidentsResolved" ? newValue : prev.incidentsResolved;

      if (reported === "" || resolved === "") {
        updated.incidentsResolutionRate = "";
        return updated;
      }
      if (
        reported !== "" &&
        resolved !== "" &&
        Number(resolved) > Number(reported)
      ) {
        updated.incidentsResolutionRate = "";
        updated.incidentsResolved = "";
        return {
          ...updated,
          incidentsResolutionRate: "",
        };
      }
      // Calculate incidentsResolutionRate if both are present and valid
      if (reported !== "" && resolved !== "" && Number(reported) > 0) {
        updated.incidentsResolutionRate = (
          (Number(resolved) / Number(reported)) *
          100
        ).toFixed(2);
      } else {
        updated.incidentsResolutionRate = "";
      }

      return updated;
    });
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };
  const handleBooleanChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === "true",
    }));
  };
  const handlePercentageChange = (e) => {
    const { value } = e.target;
    const normalizedValue = removeCommas(value);

    if (
      !/^\d*$/.test(normalizedValue) ||
      (normalizedValue !== "" &&
        (Number(normalizedValue) < 0 || Number(normalizedValue) > 100))
    )
      return;

    setAnnualFormData((prev) => ({
      ...prev,
      supplyChainAssessments: {
        ...prev.supplyChainAssessments,
        percentage: normalizedValue ? formatWithCommas(normalizedValue) : "",
      },
    }));
    setErrors((prev) => ({
      ...prev,
      supplyChainAssessments: "",
    }));
  };

  const handleSupplyChainDetailsChange = (e) => {
    const { value } = e.target;
    setAnnualFormData((prev) => ({
      ...prev,
      supplyChainAssessments: {
        ...prev.supplyChainAssessments,
        additionalDetails: value,
      },
    }));
  };

  const handleNestChange = (e) => {
    const { name, value } = e.target;
    const parts = name.split(".");
    const leaf = parts[parts.length - 1];
    setAnnualFormData((prev) => {
      const updated = { ...prev };
      let current = updated;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) current[parts[i]] = {};
        current = current[parts[i]];
      }
      current[leaf] = value;
      return updated;
    });
    if (parts[0] === "humanRightsPolicy") {
      setErrors((prev) => {
        const next = { ...prev };
        const cur =
          typeof next.humanRightsPolicy === "object" && next.humanRightsPolicy
            ? { ...next.humanRightsPolicy }
            : {};
        if (leaf === "remark" && String(value || "").trim()) delete cur.remark;
        if (leaf === "effectiveDate") delete cur.effectiveDate;
        if (Object.keys(cur).length) next.humanRightsPolicy = cur;
        else delete next.humanRightsPolicy;
        return next;
      });
    } else {
      setErrors((prev) => ({
        ...prev,
        [parts[0]]: "",
      }));
    }
  };

  const handleGrievanceRemarkChange = (e, type) => {
    const { value } = e.target;
    setAnnualFormData((prev) => ({
      ...prev,
      grievanceMechanism: {
        ...prev.grievanceMechanism,
        [type]: {
          ...prev.grievanceMechanism[type],
          remark: value,
        },
      },
    }));
  };
  const handleSubmit = async () => {
    if (validation()) return;

    // Send the appropriate formData based on module
    const dataToSubmit = module === "annual" ? annualFormData : monthlyFormData;
    const cleanedFormData =
      module === "annual"
        ? {
            ...dataToSubmit,
            humanRightsPolicy: {
              ...dataToSubmit.humanRightsPolicy,
              ...(dataToSubmit.humanRightsPolicy.status === false
                ? {
                    documentUrl: "",
                    isRevised: false,
                    remark: "",
                    effectiveDate: "",
                  }
                : {}),
            },
          }
        : dataToSubmit;

    const payload = {
      humanRights: stripCommasFromNumericStrings(cleanedFormData),
      stepsCompleted: {
        humanRights: true,
      },
    };

    await put(`/social/updateSocial?Id=${id}`, payload)
      .then((res) => {
        const entryTypeParam = module === "annual" ? "annual" : "monthly";
        if (module === "annual") {
          navigate(`/social/data/employee-benefit/${id}`);
        } else {
          navigate(`/social/data/training-development/${id}`);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const displayedHumanRightsPolicyDocument = isHumanRightsPolicyDocDeleted
    ? annualFormData?.humanRightsPolicy?.documentUrl || ""
    : annualFormData?.humanRightsPolicy?.documentUrl ||
      (annualFormData?.humanRightsPolicy?.isRevised
        ? ""
        : existingHumanRightsPolicyDocUrl ||
          prevHumanRightsPolicy?.documentUrl) ||
      "";

  return (
    <MainDashboardLayout sidebar={<SocialLayout />}>
      {/* <div style={{ padding: "1rem 0" }}>
        <span
          style={{
            fontSize: "14px",
            fontWeight: "400",
            color: "rgba(0, 0, 0, 0.8)",
          }}
        >
          Data{" "}
        </span>
        <span>
          <ChevronRightIcon />
        </span>{" "}
        <span
          style={{
            fontSize: "14px",
            fontWeight: "600",
            color: "rgba(0, 0, 0, 1)",
          }}
        >
          Add New Entry
        </span>
      </div>
      <div
        style={{
          fontSize: "24px",
          fontWeight: "700",
          color: "#001632",
          marginBottom: "1rem",
        }}
      >
        Add New Entry
      </div> */}
      <p className={styles.heading + " " + styles.form_headingStrongUpper}>
        F. Human Rights
      </p>

      {/* Annual Module */}
      {module === "annual" && (
        <>
          {/* 1. Human Rights Policy */}
          <BooleanInputField 
            label="1. Human Rights Policy:"
            name="humanRightsPolicy.status"
            value={annualFormData?.humanRightsPolicy?.status ?? true}
            onChange={handleNestedBooleanChange}
          />
          {annualFormData?.humanRightsPolicy?.status ? (
            <>
              {shouldShowAnnualPolicyRevised(
              !isFirstEntry,
                previousYearHumanRightsDoc,
              ) && (
                <BooleanInputField
                  label="Is the Policy revised?"
                  name="humanRightsPolicy.isRevised"
                  value={annualFormData?.humanRightsPolicy?.isRevised ?? false}
                  onChange={handleNestedBooleanChange}
                />
              )}
              <Box sx={{ ml: "-0.7rem" }}>
                <Typography
                  component="p"
                  className={styles.form_heading}
                  sx={{ fontWeight: "bold", ml: "1.1rem", mb: 0.5, mt: 1 }}
                >
                  Upload evidence (for Policy):
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                    ml: "1.1rem",
                  }}
                >
                  <Box
                    sx={{
                      width: hasUploadedDocument(displayedHumanRightsPolicyDocument)
                        ? "233px"
                        : "100px",
                    }}
                  >
                    <FileUpload
                      setDocumentUrl={(file) =>
                        handleFileChange(file, "humanRightsPolicy")
                      }
                      filesList={displayedHumanRightsPolicyDocument}
                      isDisabled={isAnnualPolicyUploadDisabled(
                        !isFirstEntry,
                        previousYearHumanRightsDoc,
                      )}
                    />
                    {typeof errors?.humanRightsPolicy === "object" &&
                    errors.humanRightsPolicy?.documentUrl ? (
                      <FormHelperText
                        sx={{ color: "#D34040", fontSize: "14px", minHeight: "20px" }}
                      >
                        {errors.humanRightsPolicy.documentUrl}
                      </FormHelperText>
                    ) : null}
                  </Box>
                  <Box
                    sx={{
                      width: "30%",
                      marginTop: hasUploadedDocument(
                        displayedHumanRightsPolicyDocument,
                      )
                        ? "15px"
                        : "10px",
                        ml:hasUploadedDocument(displayedHumanRightsPolicyDocument) ? "0" : "1.1rem",
                    }}
                  >
                    <TextField
                      fullWidth
                      name="humanRightsPolicy.remark"
                      value={
                        annualFormData?.humanRightsPolicy?.remark ||
                        (annualFormData?.humanRightsPolicy?.isRevised
                          ? ""
                          : prevHumanRightsPolicy?.remark) ||
                        ""
                      }
                      onChange={handleNestChange}
                      variant="outlined"
                      size="small"
                      multiline
                      minRows={1}
                      error={
                        !!(
                          typeof errors?.humanRightsPolicy === "object" &&
                          errors.humanRightsPolicy?.remark
                        )
                      }
                      InputProps={{
                        sx: { padding: "9px 12px" },
                      }}
                      placeholder="Enter Additional Text"
                    />
                    <FormHelperText
                      sx={{ color: "#D34040", fontSize: "14px", minHeight: "20px" }}
                    >
                      {typeof errors?.humanRightsPolicy === "object"
                        ? errors.humanRightsPolicy?.remark || " "
                        : " "}
                    </FormHelperText>
                  </Box>
                </Box>
                <Typography
                  component="p"
                  className={styles.form_heading}
                  sx={{ fontWeight: "bold", ml: "1.1rem",mb:"12px" }}
                >
                  Effective date (for Policy):
                </Typography>
                <TextField
                  type="date"
                  name="humanRightsPolicy.effectiveDate"
                  value={
                    annualFormData?.humanRightsPolicy?.effectiveDate ||
                    (annualFormData?.humanRightsPolicy?.isRevised
                      ? ""
                      : prevHumanRightsPolicy?.effectiveDate) ||
                    ""
                  }
                  inputProps={{
                    min: "2021-01-01",
                    ...(policyEffectiveMax
                      ? { max: policyEffectiveMax }
                      : { max: maxEffectiveDate }),
                  }}
                  onChange={handleNestChange}
                  variant="outlined"
                  size="small"
                  error={
                    !!(
                      typeof errors?.humanRightsPolicy === "object" &&
                      errors.humanRightsPolicy?.effectiveDate
                    )
                  }
                  sx={{
                    marginBottom: "0.25rem",
                    width: "30%",
                    ml: "1.1rem",
                    "& .MuiInputBase-root": { height: 40 },
                  }}
                />
                <FormHelperText
                  sx={{
                    color: "#D34040",
                    fontSize: "14px",
                    marginLeft: "1.1rem",
                    width: "30%",
                  }}
                >
                  {typeof errors?.humanRightsPolicy === "object"
                    ? errors.humanRightsPolicy?.effectiveDate
                    : ""}
                </FormHelperText>
              </Box>
            </>
          ) : (
            <div
          style={{
            marginTop: "0.5rem",
            marginBottom: "1rem",
            marginLeft: "1rem",
          }}
        >
          {/* <p className={styles.form_heading} style={{ fontWeight: "bold" }}>
            Remark
          </p> */}
          <TextField
            name="humanRightsPolicy.statusremark"
            value={annualFormData?.humanRightsPolicy?.statusremark || ""}
            onChange={handleNestChange}
            placeholder="Enter Additional Text"
            size="small"
            sx={{ width: "40%" }}
          />
        </div>
          )}

          {/* 2. Human Rights Assessments of Supply Chain */}
          <div style={{ marginBottom: "1rem", marginTop: "1rem" }}>
            <p className={styles.form_heading} style={{ fontWeight: "bold" }}>
              2. Human Rights Assessments of Supply Chain (Enter percentage of
              suppliers assessed by entity or by third party)
            </p>
            <Box
              sx={{
                display: "flex",
                gap: "1rem",
                alignItems: "flex-start",
                mt: 1,
                width: "80%",
                ml:"1.1rem"              }}
            >
              <TextField
                name="percentage"
                value={annualFormData.supplyChainAssessments?.percentage || ""}
                onChange={handlePercentageChange}
                variant="outlined"
                size="small"
                sx={{ width: "30%" }}
                placeholder="Enter Percentage"
                error={!!errors?.supplyChainAssessments}
              />
              <TextField
                name="additionalDetails"
                value={
                  annualFormData.supplyChainAssessments?.additionalDetails || ""
                }
                onChange={handleSupplyChainDetailsChange}
                variant="outlined"
                size="small"
                sx={{ width: "30%", flex: 1 }}
                placeholder="Enter Additional Text"
              />
            </Box>
            {errors?.supplyChainAssessments && (
              <FormHelperText
                sx={{ color: "#D34040", marginLeft: "1rem", fontSize: "14px" }}
              >
                {errors?.supplyChainAssessments}
              </FormHelperText>
            )}
          </div>

          {/* 3. Grievance mechanism in place */}
          <p
            className={styles.form_heading}
            style={{ fontWeight: "bold", marginBottom: "1rem" }}
          >
            3. Is there a Grievance mechanism in place
          </p>

          {/* For Community */}
          <div style={{ marginLeft: "2rem", marginBottom: "1rem" }}>
            <BooleanInputField
              label="1. For Community:"
              name="grievanceMechanism.forCommunity.status"
              value={annualFormData?.grievanceMechanism?.forCommunity?.status}
              onChange={handleNestedBooleanChange}
            />
            {annualFormData?.grievanceMechanism?.forCommunity?.status && (
              <>
                <p
                  className={styles.form_heading}
                  style={{
                    fontWeight: "bold",
                    // marginTop: "1rem",
                    marginLeft: "1rem",
                  }}
                >
                  Upload evidence
                </p>
                <Box
                  sx={{
                    display: "flex",
                    gap: "1rem",
                    alignItems: "flex-start",
                    mt: 1,
                    ml: "1rem",
                  }}
                >
                  <FileUpload
                    setDocumentUrl={(file) =>
                      handleFileChange(
                        file,
                        "grievanceMechanism",
                        "forCommunity",
                      )
                    }
                    mt="6px"
                    filesList={
                      annualFormData.grievanceMechanism?.forCommunity
                        ?.documentUrl
                    }
                    isDisabled={true}
                  />
                  <Box
                    sx={{ display: "flex", flexDirection: "column", flex: 1 }}
                  >
                    <TextField
                      value={
                        annualFormData.grievanceMechanism?.forCommunity
                          ?.remark || ""
                      }
                      onChange={(e) =>
                        handleGrievanceRemarkChange(e, "forCommunity")
                      }
                      variant="outlined"
                      size="small"
                      sx={{ mt: 1, width: "40%" }}
                      placeholder="Enter Additional Text"
                      multiline
                    />
                  </Box>
                </Box>
                {errors?.grievanceMechanism?.forCommunity?.documentUrl && (
                  <FormHelperText
                    sx={{
                      color: "#D34040",
                      marginLeft: "1.1rem",
                      fontSize: "14px",
                      mt: 0.5,
                    }}
                  >
                    {errors.grievanceMechanism.forCommunity.documentUrl}
                  </FormHelperText>
                )}
              </>
            )}
          </div>

          {/* For Employee & Workers */}
          <div style={{ marginLeft: "2rem", marginBottom: "1rem" }}>
            <BooleanInputField
              label="2. For Employee & Workers:"
              name="grievanceMechanism.forEmployeeWorkers.status"
              value={
                annualFormData?.grievanceMechanism?.forEmployeeWorkers?.status
              }
              onChange={handleNestedBooleanChange}
            />
            {annualFormData?.grievanceMechanism?.forEmployeeWorkers?.status && (
              <>
                <p
                  className={styles.form_heading}
                  style={{
                    fontWeight: "bold",
                    // marginTop: "1rem",
                    marginLeft: "1rem",
                  }}
                >
                  Upload evidence
                </p>
                <Box
                  sx={{
                    display: "flex",
                    gap: "1rem",
                    alignItems: "flex-start",
                    mt: 1,
                    ml: "1rem",
                  }}
                >
                  <FileUpload
                    setDocumentUrl={(file) =>
                      handleFileChange(
                        file,
                        "grievanceMechanism",
                        "forEmployeeWorkers",
                      )
                    }
                    mt="6px"
                    filesList={
                      annualFormData.grievanceMechanism?.forEmployeeWorkers
                        ?.documentUrl
                    }
                    isDisabled={true}
                  />
                  <Box
                    sx={{ display: "flex", flexDirection: "column", flex: 1 }}
                  >
                    <TextField
                      value={
                        annualFormData.grievanceMechanism?.forEmployeeWorkers
                          ?.remark || ""
                      }
                      onChange={(e) =>
                        handleGrievanceRemarkChange(e, "forEmployeeWorkers")
                      }
                      variant="outlined"
                      size="small"
                      sx={{ mt: 1, width: "40%" }}
                      placeholder="Enter Additional Text"
                      multiline
                    />
                  </Box>
                </Box>
                {errors?.grievanceMechanism?.forEmployeeWorkers
                  ?.documentUrl && (
                  <FormHelperText
                    sx={{
                      color: "#D34040",
                      marginLeft: "1.1rem",
                      fontSize: "14px",
                      mt: 0.5,
                    }}
                  >
                    {errors.grievanceMechanism.forEmployeeWorkers.documentUrl}
                  </FormHelperText>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Monthly Module */}
      {module === "monthly" && (
        <>
          <NumericInputField
            label="1. Number of Human Rights Incidents Reported:"
            name="incidentsReported"
            value={monthlyFormData?.incidentsReported}
            onChange={handleNumberChange}
            placeholder="Enter Number"
            errors={errors}
          />
          <NumericInputField
            label="2. Number of Human Rights Incidents Resolved:"
            name="incidentsResolved"
            value={monthlyFormData.incidentsResolved}
            onChange={handleNumberChange}
            placeholder="Enter Number"
            errors={errors}
          />
          <NumericInputField
            label="3. Resolution Rate of Human Rights Incidents:"
            name="incidentsResolutionRate"
            value={monthlyFormData.incidentsResolutionRate}
            onChange={handleNumberChange}
            placeholder="Enter Number"
            errors={errors}
            disabled={true}
          />
        </>
      )}

      <Box
        sx={{
          width: "100%",
          mt: "10%",
          mb: 5,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button
          variant="outlined"
          sx={backButtonSx}
          onClick={() => navigate(`/social/data/health-safety/${id}`)}
        >
          <WestIcon sx={{ mr: 2 }} /> Back
        </Button>
        <Button
          variant="contained"
          className={styles.submit_button}
          onClick={handleSubmit}
          sx={primaryButtonSx}
        >
          Save & Continue <ArrowForwardIcon sx={{ ml: 1 }} />
        </Button>
      </Box>
    </MainDashboardLayout>
  );
};

export default HumanRights;
